/**
 * W01d-D3 — confirmed-generation keying for the private subtree.
 *
 * The generation authority is the fixed one (identity-scope.ts, the same
 * singleton the AuthProvider commits to and the toast/cache purge contents
 * read) — this module adds NO second generation system. `useGenerationKey`
 * returns the CONFIRMED generation while the identity holds, and collapses
 * to 'public' for every non-authenticated lifecycle state; a generation
 * change therefore remounts whatever is rendered with it as a React key.
 */

import { useSyncExternalStore } from 'react';
import { appIdentityScope, type IdentityScopeState } from './identity-scope';

/** The subtree key for the identity-none states (D3: never reuses private state). */
const COLLAPSED_GENERATION_KEY = 'identity:none';

/**
 * Pure, test-reachable key computation (W01d-D3): the confirmed-generation
 * string for an authenticated snapshot, or the collapsed sentinel for any
 * non-authenticated lifecycle state. The hook below delegates here so the
 * production keying and the tests share ONE implementation.
 */
export function computeGenerationKey(
  confirmedIdentity: boolean,
  state: Pick<IdentityScopeState, 'userId' | 'workspaceId' | 'generation'> = appIdentityScope.current()
): string {
  if (!confirmedIdentity || !state.userId || !state.workspaceId) {
    return COLLAPSED_GENERATION_KEY;
  }
  return `identity:${state.userId}:${state.workspaceId}:gen${state.generation}`;
}

function subscribeToScope(onChange: () => void): () => void {
  return appIdentityScope.onInvalidate(() => onChange());
}

function getScopeSnapshot(): IdentityScopeState {
  return appIdentityScope.current();
}

/**
 * The confirmed-generation string key. While a confirmed identity exists,
 * the key tracks the exact generation (mounts a fresh subtree per
 * generation); during unauthenticated/verifying/transitioning/unavailable
 * or a null-identity collapse the key is the collapsed sentinel, so no
 * stale component-local state can be reused across transitions.
 */
export function useGenerationKey(confirmedIdentity: boolean): string {
  const state: IdentityScopeState = useSyncExternalStore(
    subscribeToScope,
    getScopeSnapshot,
    getScopeSnapshot,
  );
  return computeGenerationKey(confirmedIdentity, state);
}
